const API_BASE_URL = '/api/documentation/jobs/';

// Helper to get auth headers
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

// Helper function to get current user ID
const getCurrentUserId = () => {
    try {
        const userId = localStorage.getItem('kk_user_id') ||
            localStorage.getItem('user_id') ||
            localStorage.getItem('userId');

        if (userId) {
            return parseInt(userId);
        }

        const userDataString = localStorage.getItem('kk_user') || localStorage.getItem('user');
        if (userDataString) {
            const userData = JSON.parse(userDataString);
            if (userData.id) {
                return parseInt(userData.id);
            }
            if (userData.user_id) {
                return parseInt(userData.user_id);
            }
        }
        return null;
    } catch (error) {
        console.error('Error getting user ID:', error);
        return null;
    }
};

/**
 * Send email for Client Statements and save to database.
 * Mirrors sendAndSaveEmail from emailService.js, but additionally supports
 * generated attachments (debit note, customer statement PDF) that are not
 * JobDocument records — entries with a string id and an attach_url/url are
 * fetched as blobs and uploaded as file attachments.
 *
 * @param {Object} emailData - Email data from the modal
 * @param {number} jobId - Job ID to associate the email with
 * @param {Object} statementContext - { supplier_name, invoice_no, bl_no, balance_display }
 * @returns {Promise<Object>} Response from the server
 */
export const sendAndSaveStatementEmail = async (emailData, jobId, statementContext) => {
    try {
        const formData = new FormData();

        const userId = getCurrentUserId();
        if (!userId) {
            throw new Error('User ID not found. Please log in again.');
        }

        // Statement details and the signature are rendered in the EmailModal
        // body itself (bodyAppendHtml) — nothing is appended here or backend-side.
        const emailPayload = {
            subject: emailData.subject,
            body: emailData.body || '',
            from_email: emailData.from,
            to_email: emailData.to,
            cc_email: emailData.cc || '',
            email_date: emailData.date,
            sent_by: userId,
            statement_id: statementContext?.statement_id || null,
            skip_signature: true
        };

        formData.append('email_data', JSON.stringify(emailPayload));

        // Add file attachments
        let attachmentIdx = 0;
        if (emailData.attachments && emailData.attachments.length > 0) {
            emailData.attachments.forEach((file) => {
                formData.append(`attachment_${attachmentIdx}`, file);
                attachmentIdx += 1;
            });
        }

        // Split selected attachments: numeric ids are JobDocuments attached
        // server-side; string ids with a URL are generated docs fetched here.
        if (emailData.selectedJobAttachments && emailData.selectedJobAttachments.length > 0) {
            const jobAttachmentIds = [];
            const generatedAttachments = [];
            emailData.selectedJobAttachments.forEach((att) => {
                const generatedUrl = att.attach_url || att.url;
                if (typeof att.id === 'string' && generatedUrl) {
                    generatedAttachments.push(att);
                } else {
                    jobAttachmentIds.push(att.id);
                }
            });
            formData.append('job_attachment_ids', JSON.stringify(jobAttachmentIds));

            for (const att of generatedAttachments) {
                try {
                    const res = await fetch(att.attach_url || att.url, { headers: getAuthHeaders(true) });
                    if (!res.ok) continue;
                    const blob = await res.blob();
                    const name = att.name || att.file_name || 'attachment';
                    formData.append(`attachment_${attachmentIdx}`, new File([blob], name, { type: blob.type || 'application/octet-stream' }));
                    attachmentIdx += 1;
                } catch (err) {
                    console.warn('Could not fetch generated attachment:', att.attach_url || att.url, err);
                }
            }
        }

        const response = await fetch(`${API_BASE_URL}${jobId}/send-email/`, {
            method: 'POST',
            headers: getAuthHeaders(true),
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.text();
            console.error('Response error:', errorData);
            throw new Error(`Failed to send email: ${response.status} - ${errorData}`);
        }

        const result = await response.json();

        return {
            success: result.success || true,
            message: result.message || 'Email sent and saved successfully',
            data: result
        };

    } catch (error) {
        console.error('Statement email service error:', error);
        return {
            success: false,
            message: error.message || 'Failed to send email',
            error: error
        };
    }
};
